import { type Theme, css } from '@emotion/react'
import {
  BREAKPOINT_580,
  BREAKPOINT_870,
  BREAKPOINT_1440,
  Z_INDEX_GROUP_1,
  Z_INDEX_GROUP_2,
  Z_INDEX_GROUP_9,
  Z_INDEX_OVERLAY,
} from '@zcode-ui/themes'
import {
  gray10,
  gray30,
  gray50,
  gray70,
  gray90,
  newGreen,
  slateDarker,
} from '../../../../../../styles/colors'

export const noPicImage = css`
  object-fit: cover;
`

export const image = css`
  width: 100%;
  height: 100%;

  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;

  transition: transform 0.15s;

  label: image;
`

export const imagesPill = ({ typography }: Theme) => css`
  position: absolute;
  bottom: 15px;
  left: 15px;

  padding: 6px 11px;

  color: white;
  font-weight: ${typography?.fontWeightBold ?? 'bold'};
  font-size: 12px;
  line-height: 100%;

  background: rgba(0, 0, 0, 0.4);
  border-radius: 50px;
  visibility: hidden;
  opacity: 0;

  transition: opacity 0.15s;

  label: imagesPill;
`

export const listItem = css`
  width: 100%;
  margin: 0 auto;
  padding: 0;

  /* border: 0.5px solid ${gray30}; */
  border-radius: 8px;

  cursor: pointer;

  transition: box-shadow 0.15s;

  &:hover {
    box-shadow: 0 2px 12px rgba(173, 178, 194, 0.16);

    .css-${image.name} {
      transform: scale3d(1.02, 1.02, 1);
    }

    .css-${imagesPill.name} {
      visibility: visible;
      opacity: 1;
    }
  }
`

export const header = css`
  display: flex;
  align-items: center;
  margin-top: 15px;
`

export const headerWithInfo = css`
  padding-right: 80px;
`

export const headerText = css`
  margin-right: 5px;
  overflow: hidden;

  color: '#000';
  font-size: 15px;
  line-height: 120%;
  white-space: nowrap;
  text-overflow: ellipsis;
`

export const headerTextStrong = ({ typography }: Theme) => css`
  font-weight: ${typography?.fontWeightBold ?? 'bold'};
`

export const locationText = css`
  display: flex;
  margin-top: 12px;

  color: ${gray70};
  font-size: 15px;
  line-height: 120%;
  white-space: nowrap;

  .hood {
    color: ${gray90};
  }
`

export const hide = css`
  z-index: -1;
`

export const displayNone = css`
  display: none;
`

export const address = css`
  display: block;
  overflow: hidden;

  color: ${gray70};
  text-overflow: ellipsis;

  &:hover {
    color: ${gray70};
    text-decoration: none;
  }
`

export const propertyTypeText = ({ typography }: Theme) => css`
  margin: 0 0 0 5px;

  font-weight: ${typography?.fontWeightRegular ?? 'normal'};
`

export const verified = css`
  position: relative;

  display: flex;
  flex-direction: column-reverse;
  align-items: center;
  justify-content: center;
  height: 15px;
  margin: 0;
  padding: 0;

  background: transparent;
  border: none;

  &:matches(:focus, :hover) > .verifiedTooltipContainer .verifiedTooltip {
    visibility: visible;
    opacity: 1;
  }
`

export const verifiedTooltipContainer = css`
  position: absolute;
  bottom: 50px;
  z-index: ${Z_INDEX_GROUP_1};

  width: 216px;

  transform: translateY(50%);
`

export const verifiedTooltip = ({ typography }: Theme) => css`
  position: absolute;
  z-index: ${Z_INDEX_GROUP_9};

  padding: 4px 8px;

  color: #fff;
  font-weight: ${typography?.fontWeightBold ?? 'bold'};
  font-size: 12px;

  background: ${slateDarker};
  border-radius: 3px;
  visibility: hidden;
  opacity: 0;

  transition: opacity 0.3s;

  &::before {
    bottom: calc(5px * -1);
    right: 50%;
    position: absolute;

    display: inline-block;

    content: '';
    border-right: 5px solid transparent;
    border-left: 5px solid transparent;
    transform: translateX(50%);
    border-top: 5px solid ${slateDarker};
  }

  &::after {
    bottom: calc((5px - 1px) * -1);

    border-top: calc(5px - 1px) solid transparent;
    right: 50%;
    position: absolute;

    display: inline-block;

    content: '';
    border-right: calc(5px - 1px) solid transparent;
    border-left: calc(5px - 1px) solid transparent;
    transform: translateX(50%);
  }
`

export const badges = css`
  display: grid;
  grid-template-columns: repeat(2, max-content);
  gap: 4px;

  position: absolute;
  top: 10px;
  left: 15px;
`

export const price = ({ colors }: Theme) => css`
  margin-right: 10px;
  font-size: 22px;
  line-height: 22px;
`

export const unavailablePrice = ({ colors }: Theme) => css`
  color: ${colors?.text ?? '#000'};
`

export const noPrice = ({ colors }: Theme) => css`
  color: ${colors?.text ?? '#697179'};
`

export const priceAndIcons = css`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`

export const priceAndViewing = css`
  display: flex;
  align-items: center;
`
export const icons = css`
  display: flex;
  align-items: center;
`

export const content = css`
  position: relative;
  display: flex;
  align-items: center;
  height: 230px;
  justify-content: space-between;
`

export const largeCard = css`
  height: 300px;
`

export const contact = css`
  margin-top: 4px;
`

export const infoOverlay = css`
  display: none;

  &.showInfoOverlay {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;

    z-index: ${Z_INDEX_OVERLAY};

    display: block;

    padding: 19px 14px 14px;

    background: #fff;
  }
`

export const infoOverlayCloseBtn = css`
  position: absolute;
  top: 19px;
  right: 14px;

  width: 16px;
  height: 16px;
`

export const infoOverlayText = css`
  max-height: 105px;
  padding-right: 15px;

  overflow: hidden;
`
export const showFullBtn = css`
  margin: 1px;
`

export const infoOverlayContact = css`
  position: absolute;
  bottom: 14px;
`

export const imageContainer = css`
  position: relative;

  width: 325px;
  height: 100%;
  overflow: hidden;

  border-top-left-radius: 8px;
  border-bottom-left-radius: 8px;
`

export const noImage = css`
  width: 100%;
  height: 100%;

  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;

  transition: transform 0.15s;
`

export const infoContent = css`
  display: flex;
  flex: 1 1;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  padding: 20px;
  position: relative;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  border-radius: 0 0 8px 8px;

  @media (min-width: ${BREAKPOINT_870}) and (max-width: ${BREAKPOINT_1440}) {
    padding: 16px;
  }

  @media (max-width: ${BREAKPOINT_580}) {
    padding: 14px 0;
  }
`

export const infoIcon = css`
  right: 20px;
  position: absolute;
  @media (min-width: ${BREAKPOINT_870}) and (max-width: ${BREAKPOINT_1440}) {
    right: 16px;
  }

  @media (max-width: ${BREAKPOINT_580}) {
    right: 0;
  }
`

export const amenities = css`
  display: flex;
  justify-content: flex-start;
  margin-top: 15px;
`

export const amenity = ({ colors }: Theme) => css`
  margin-right: 4px;
  padding: 4px 6px;

  color: ${colors?.textLight ?? '#454442'};
  font-size: 13px;
  line-height: 100%;
  vertical-align: middle;

  background-color: ${colors?.background ?? '#EBEAE6'};
  border-radius: 4px;
`

export const unavailableAmenity = css`
  color: ${gray90};

  background: ${gray10};
`

export const bedBathAge = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 6px;
`
export const bedBathInfo = css`
  display: flex;
  align-items: center;
`

export const bedBathText = ({ colors, typography }: Theme) => css`
  margin: 0;
  font-size: ${typography?.fontSize14 ?? '14px'};
  line-height: 100%;
`

export const unavailableText = ({ colors }: Theme) => css`
  color: ${colors?.textLightest ?? '#726f6c'};
  font-size: 15px;
  line-height: 100%;
`

export const ageText = ({ colors, typography }: Theme) => css`
  font-size: ${typography?.fontSize12 ?? '12px'};
`

export const viewing = css`
  display: flex;
  align-items: center;
  padding: 4px 6px;

  background-color: ${newGreen};
  border-radius: 4px;
`

export const viewingText = css`
  padding-left: 6px;

  color: #fff;
  font-size: 13px;
  line-height: 100%;
`
export const overlay = css`
  position: absolute;
  z-index: ${Z_INDEX_GROUP_1};

  max-width: 49%;
  height: 100%;
  margin-top: -10px;

  color: '#000';

  background: #fff;

  &.show {
    z-index: ${Z_INDEX_GROUP_1};
  }

  &:focus {
    outline: none;
  }
`

export const overlayContent = css`
  max-width: 340px;
  height: 100%;
`

export const overlayInfo = css`
  display: -webkit-box;
  padding-top: 8px;
  overflow: hidden;

  font-size: 15px;
  line-height: 150%;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
`

export const largeCardOverlay = css`
  -webkit-line-clamp: 5;

  line-height: 175%;
`

export const moreInfo = ({ colors }: Theme) => css`
  padding-top: 3px;

  color: ${colors?.primary ?? '#2e64e2'};
  font-size: 15px;
`

export const seoExperimentText = css`
  padding: 14px;
`

// favorite button style
export const favoriteIconContainer = css`
  position: relative;
  z-index: ${Z_INDEX_GROUP_2};

  margin: 0 0 0 10px;
`

export const btnIcon = css`
  position: relative;

  display: flex;
  justify-content: center;
  padding: 0;

  background-color: transparent;
  border: none;
  outline-offset: -4px;
  cursor: pointer;
`

export const iconContainer = css`
  display: flex;
  flex: 1;
  justify-content: flex-end;
`

export const favoriteHoverOverlay = css`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: ${Z_INDEX_GROUP_1};
`
export const unavailableBtn = ({ typography }: Theme) => css`
  width: 100%;
  height: 40px;

  color: ${gray90};
  font-weight: ${typography?.fontWeightBold ?? 'bold'};
  font-size: 14px;

  background: #fff;
  border: 0.75px solid ${gray50};
  border-radius: 4px;
`
